import React, { useState, useCallback, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { 
  DollarSign, 
  TrendingUp, 
  Building, 
  PiggyBank,
  Target,
  Percent,
  BarChart3,
  GripVertical,
  Home,
  Calendar,
  Landmark,
  Wallet,
  CreditCard,
  ArrowUpRight,
  BadgeDollarSign,
  CircleDollarSign,
  HandCoins,
  ClipboardCheck,
  Clock,
  Key,
  LineChart,
  ChevronDown,
  ChevronUp
} from 'lucide-react';

const formatCurrency = (value) => {
  if (value === undefined || value === null) return 'R$ 0,00';
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
    minimumFractionDigits: 2,
  }).format(value);
};

const formatPercent = (value, digits = 2) => {
  if (value === undefined || value === null || !isFinite(Number(value))) return '0' + (digits > 0 ? ',' + '0'.repeat(digits) : '') + '%';
  return Number(value).toLocaleString('pt-BR', { minimumFractionDigits: digits, maximumFractionDigits: digits }) + '%';
};

// Lista colapsável de balões (Ver mais / Ver menos quando exceder `limit`)
const CollapsibleBaloes = ({ items, renderItem, limit = 5, accentClass = 'text-slate-700' }) => {
  const [expanded, setExpanded] = useState(false);
  if (!items || items.length === 0) return null;
  const visible = expanded || items.length <= limit ? items : items.slice(0, limit);
  const hidden = items.length - visible.length;
  return (
    <>
      <div className="space-y-2">
        {visible.map((b, i) => renderItem(b, i))}
      </div>
      {items.length > limit && (
        <button
          type="button"
          data-testid="btn-ver-mais-baloes"
          onClick={() => setExpanded(e => !e)}
          className={`mt-2 w-full flex items-center justify-center gap-1.5 py-2 text-xs font-semibold rounded-lg border border-slate-200 bg-white hover:bg-slate-50 transition-colors ${accentClass}`}
        >
          {expanded ? (
            <>
              <ChevronUp size={14} />
              Ver menos
            </>
          ) : (
            <>
              <ChevronDown size={14} />
              Ver mais ({hidden} {hidden === 1 ? 'oculto' : 'ocultos'})
            </>
          )}
        </button>
      )}
    </>
  );
};

const MetricCard = ({ title, value, icon: Icon, highlight, subtext, valueColor }) => {
  return (
    <div className={`metric-card card-hover ${highlight ? 'highlight-card' : ''}`}>
      <div className="flex items-start justify-between mb-3">
        <div className={`p-2 rounded-lg ${highlight ? 'bg-emerald-200/50' : 'bg-slate-100'}`}>
          <Icon className={highlight ? 'text-primary' : 'text-slate-600'} size={20} />
        </div>
      </div>
      <p className="data-label">{title}</p>
      <p className={`data-value ${valueColor || (highlight ? 'text-primary' : 'text-slate-900')}`}>
        {typeof value === 'string' ? value : formatCurrency(value)}
      </p>
      {subtext && <p className="text-xs text-slate-500 mt-1">{subtext}</p>}
    </div>
  );
};

export const ResultsSummary = ({ result, formData }) => {
  // Estado para o mês selecionado no slider - DEVE estar antes de qualquer return
  const [selectedMes, setSelectedMes] = useState(null);
  const sliderRef = useRef(null);
  const isDragging = useRef(false);

  // Handler para arrastar o ponto - DEVE estar antes de qualquer return
  const handleSliderInteraction = useCallback((e, isTouch = false) => {
    if (!sliderRef.current || !result) return;
    const rect = sliderRef.current.getBoundingClientRect();
    const clientX = isTouch ? e.touches[0].clientX : e.clientX;
    const x = Math.max(0, Math.min(clientX - rect.left, rect.width));
    const percent = x / rect.width;
    const prazo = formData?.prazo_meses || 100;
    const mes = Math.max(1, Math.min(prazo, Math.round(percent * prazo)));
    setSelectedMes(mes);
  }, [formData?.prazo_meses, result]);

  const handleMouseDown = useCallback((e) => {
    isDragging.current = true;
    handleSliderInteraction(e);
  }, [handleSliderInteraction]);

  const handleMouseMove = useCallback((e) => {
    if (isDragging.current) {
      handleSliderInteraction(e);
    }
  }, [handleSliderInteraction]);

  const handleMouseUp = useCallback(() => {
    isDragging.current = false;
  }, []);

  const handleTouchStart = useCallback((e) => {
    isDragging.current = true;
    handleSliderInteraction(e, true);
  }, [handleSliderInteraction]);

  const handleTouchMove = useCallback((e) => {
    if (isDragging.current) {
      handleSliderInteraction(e, true);
    }
  }, [handleSliderInteraction]);

  if (!result) return null;

  // Pegar dados do mês 36 (3 anos) se disponível
  const mes36 = result.projecoes.find(p => p.mes === 36);
  const mesFinal = result.projecoes[result.projecoes.length - 1];

  // Total da proposta completa (todas as parcelas simuladas)
  const totalProposta = result.total_proposta;
  const totalInvestidoFinal = mesFinal ? mesFinal.total_investido_corrigido : 0;

  // Calcular financiamento pós-obra 
  // Sempre calcular no frontend para garantir funcionamento mesmo com cache
  const conclusaoObraMes = result.conclusao_obra_mes || 0;
  const prazoMeses = formData?.prazo_meses || result.projecoes.length;
  
  let financiamentoPosObra = 0;
  let financiamentoPosObraPercentual = 0;
  let mesesPosObra = 0;

  // Se o usuário escolheu "Financiamento Bancário" e informou o valor, usa-o diretamente
  // (vem do backend como result.financiamento_bancario — já somado em total_proposta)
  if (formData?.tipo_financiamento === 'bancario' && (result.financiamento_bancario || 0) > 0) {
    financiamentoPosObra = Number(result.financiamento_bancario) || 0;
    financiamentoPosObraPercentual = Number(result.financiamento_bancario_percentual) || 0;
    // Meses pós-obra: se prazo > conclusão, usa diferença; senão, 0 (é um valor externo)
    mesesPosObra = (conclusaoObraMes > 0 && prazoMeses > conclusaoObraMes) ? (prazoMeses - conclusaoObraMes) : 0;
  }
  // Caso contrário (financiamento "direto"): calcula a partir das parcelas pós-obra
  else if (conclusaoObraMes > 0 && prazoMeses > conclusaoObraMes) {
    mesesPosObra = prazoMeses - conclusaoObraMes;
    financiamentoPosObra = result.projecoes
      .filter(p => p.mes > conclusaoObraMes)
      .reduce((sum, p) => sum + (p.parcela_original || 0) + (p.reforco_original || 0), 0);
    financiamentoPosObraPercentual = formData?.valor_compra > 0 
      ? (financiamentoPosObra / formData.valor_compra * 100) 
      : 0;
  }

  // Marcos dinâmicos
  const mesMeioTorre = result.meio_torre_mes ? result.projecoes.find(p => p.mes === result.meio_torre_mes) : null;
  const mesChaves = result.conclusao_obra_mes ? result.projecoes.find(p => p.mes === result.conclusao_obra_mes) : null;

  // Dados do mês selecionado
  const mesSelecionado = selectedMes ? result.projecoes.find(p => p.mes === selectedMes) : null;

  // Componente reutilizável para exibir contexto do total simulado
  const TotalSimuladoBar = ({ mesAtual }) => {
    const investidoAteMes = mesAtual?.total_investido_corrigido || 0;
    const percentPago = totalInvestidoFinal > 0 ? (investidoAteMes / totalInvestidoFinal * 100) : 0;
    const restante = totalInvestidoFinal - investidoAteMes;
    return (
      <div className="mt-4 p-3 bg-slate-100 rounded-lg border border-slate-200">
        <div className="flex justify-between items-center text-xs text-slate-600 mb-2">
          <span>Pago até o mês {mesAtual?.mes}</span>
          <span>Total simulado ({formData.prazo_meses} meses)</span>
        </div>
        <div className="w-full bg-slate-300 rounded-full h-2 mb-2">
          <div className="bg-emerald-600 h-2 rounded-full transition-all" style={{ width: `${Math.min(100, percentPago)}%` }} />
        </div>
        <div className="flex justify-between items-center">
          <div>
            <p className="text-sm font-mono font-semibold text-slate-800">{formatCurrency(investidoAteMes)}</p>
            <p className="text-xs text-slate-500">{formatPercent(percentPago, 1)} do total</p>
          </div>
          <div className="text-right">
            <p className="text-sm font-mono font-semibold text-slate-800">{formatCurrency(totalInvestidoFinal)}</p>
            <p className="text-xs text-slate-500">Restante: {formatCurrency(restante)}</p>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Estrutura da Proposta */}
      <Card className="bg-white border-slate-200">
        <CardHeader className="pb-4">
          <CardTitle className="flex items-center gap-2 text-lg text-slate-800">
            <DollarSign className="text-primary" size={20} />
            Proposta
          </CardTitle>
        </CardHeader>
        <CardContent>
          {(() => {
            const hasFinanciamentoBancario = formData?.tipo_financiamento === 'bancario' && (result.financiamento_bancario || 0) > 0;
            const hasVeiculos = (result.veiculos_total || 0) > 0;
            const hasChaves = (result.parcela_chaves_valor || 0) > 0;
            const extras = (hasFinanciamentoBancario ? 1 : 0) + (hasVeiculos ? 1 : 0) + (hasChaves ? 1 : 0);
            // Tailwind precisa de classes literais
            const gridCols = extras >= 3
              ? 'grid-cols-2 md:grid-cols-7'
              : extras === 2
                ? 'grid-cols-2 md:grid-cols-6'
                : extras === 1
                  ? 'grid-cols-2 md:grid-cols-5'
                  : 'grid-cols-2 md:grid-cols-4';
            return (
          <div className={`grid ${gridCols} gap-4`}>
            <div className="text-center p-4 bg-red-50 rounded-lg border border-red-100">
              <HandCoins className="mx-auto mb-1 text-red-600" size={20} />
              <p className="text-xs uppercase tracking-wider text-red-700 mb-1">Entrada (Ato)</p>
              <p className="text-xl font-mono font-semibold text-red-800">
                {formatCurrency(result.ato_sinal || 0)}
              </p>
              <p className="text-xs text-red-600 mt-1">
                {(result.ato_sinal_percentual || 0).toFixed(2)}%
              </p>
              {result.sinal > 0 && (
                <p className="text-[10px] text-red-500 mt-1 leading-tight">
                  Sinal pré: {formatCurrency(result.sinal)}<br/>
                  Na assinatura: {formatCurrency(result.valor_no_ato || (result.ato_sinal - result.sinal))}
                </p>
              )}
            </div>
            
            <div className="text-center p-4 bg-slate-50 rounded-lg border border-slate-200">
              <CreditCard className="mx-auto mb-1 text-slate-500" size={20} />
              <p className="text-xs uppercase tracking-wider text-slate-600 mb-1">Parcelas</p>
              <p className="text-xl font-mono font-semibold text-slate-800">
                {formatCurrency(result.parcelas_total)}
              </p>
              <p className="text-xs text-slate-500 mt-1">
                {formatCurrency(result.parcela_mensal)}/mês
              </p>
              <p className="text-xs text-slate-400">{formatPercent(result.parcelas_percentual, 2)}</p>
            </div>
            
            <div className="text-center p-4 bg-slate-50 rounded-lg border border-slate-200">
              <ArrowUpRight className="mx-auto mb-1 text-slate-500" size={20} />
              <p className="text-xs uppercase tracking-wider text-slate-600 mb-1">Reforços</p>
              <p className="text-xl font-mono font-semibold text-slate-800">
                {formatCurrency(result.reforcos_total)}
              </p>
              <p className="text-xs text-slate-500 mt-1">
                {formatPercent(result.reforcos_percentual, 1)}
              </p>
              <p className="text-xs text-slate-400">{formatPercent(result.reforcos_percentual, 2)}</p>
            </div>

            {hasFinanciamentoBancario && (
              <div className="text-center p-4 bg-indigo-50 rounded-lg border border-indigo-200">
                <Landmark className="mx-auto mb-1 text-indigo-600" size={20} />
                <p className="text-xs uppercase tracking-wider text-indigo-700 mb-1">Financ. Pós Obra</p>
                <p className="text-xl font-mono font-semibold text-indigo-800">
                  {formatCurrency(result.financiamento_bancario)}
                </p>
                <p className="text-xs text-indigo-600 mt-1">
                  {formatPercent(result.financiamento_bancario_percentual || 0, 2)}
                </p>
              </div>
            )}

            {hasVeiculos && (
              <div className="text-center p-4 bg-sky-50 rounded-lg border border-sky-200">
                <CreditCard className="mx-auto mb-1 text-sky-600" size={20} />
                <p className="text-xs uppercase tracking-wider text-sky-700 mb-1">Veículos</p>
                <p className="text-xl font-mono font-semibold text-sky-800">
                  {formatCurrency(result.veiculos_total)}
                </p>
                <p className="text-xs text-sky-600 mt-1">
                  {formatPercent(result.veiculos_percentual || 0, 2)}
                </p>
              </div>
            )}

            {hasChaves && (
              <div className="text-center p-4 bg-amber-50 rounded-lg border border-amber-200">
                <ArrowUpRight className="mx-auto mb-1 text-amber-600" size={20} />
                <p className="text-xs uppercase tracking-wider text-amber-700 mb-1">Parc. Chaves</p>
                <p className="text-xl font-mono font-semibold text-amber-800">
                  {formatCurrency(result.parcela_chaves_valor)}
                </p>
                <p className="text-xs text-amber-600 mt-1">
                  {formatPercent(result.parcela_chaves_percentual || 0, 2)}
                </p>
              </div>
            )}

            <div className="text-center p-4 bg-emerald-900 rounded-lg">
              <Wallet className="mx-auto mb-1 text-emerald-300" size={20} />
              <p className="text-xs uppercase tracking-wider text-emerald-200 mb-1">Total</p>
              <p className="text-xl font-mono font-semibold text-white">
                {formatCurrency(result.total_proposta)}
              </p>
              <p className="text-xs text-emerald-300 mt-1">
                {formatPercent(
                  formData.valor_compra > 0
                    ? (result.total_proposta / formData.valor_compra) * 100
                    : 0,
                  2
                )}
              </p>
            </div>
          </div>
            );
          })()}
        </CardContent>
      </Card>

      {/* Balões Gerados — separados em Marcos da Construção, Parcelas da Entrada e Reforços */}
      {result.baloes_gerados && result.baloes_gerados.length > 0 && (() => {
        const marcos   = result.baloes_gerados.filter(b => b.tipo === 'meio_torre' || b.tipo === 'chaves');
        const divisao  = result.baloes_gerados.filter(b => b.tipo === 'divisao_ato');
        const reforcos = result.baloes_gerados.filter(b => b.tipo !== 'meio_torre' && b.tipo !== 'chaves' && b.tipo !== 'divisao_ato');
        const totalMarcos   = marcos.reduce((s, b) => s + b.valor, 0);
        const totalDivisao  = divisao.reduce((s, b) => s + b.valor, 0);
        const totalReforcos = reforcos.reduce((s, b) => s + b.valor, 0);
        const totalGeral    = totalMarcos + totalDivisao + totalReforcos;

        // Detecta tipo de reforço (maioria)
        const tipoReforcoLabel = (() => {
          const tipos = reforcos.map(b => b.tipo);
          if (tipos.every(t => t === 'anual')) return 'Reforços Anuais';
          if (tipos.every(t => t === 'semestral')) return 'Reforços Semestrais';
          if (tipos.every(t => t === 'trimestral')) return 'Reforços Trimestrais';
          return 'Reforços Programados';
        })();

        const BalaoRow = ({ balao, isMarco }) => (
          <div
            data-testid={`balao-${balao.tipo}-${balao.mes}`}
            className={`flex items-center justify-between p-3 rounded-lg border ${
              balao.tipo === 'chaves'
                ? 'bg-emerald-50 border-primary/30'
                : balao.tipo === 'meio_torre'
                ? 'bg-blue-50 border-blue-300'
                : isMarco
                ? 'bg-amber-50 border-amber-200'
                : 'bg-slate-50 border-slate-200'
            }`}
          >
            <div className="flex items-center gap-3">
              <span className={`text-xs font-bold px-2 py-1 rounded ${
                balao.tipo === 'chaves'
                  ? 'bg-emerald-200 text-primary'
                  : balao.tipo === 'meio_torre'
                  ? 'bg-blue-200 text-blue-800'
                  : isMarco
                  ? 'bg-amber-200 text-amber-800'
                  : 'bg-slate-200 text-slate-700'
              }`}>
                Mês {balao.mes}
              </span>
              <div>
                <p className="text-sm font-medium text-slate-800">{balao.descricao}</p>
                <p className="text-xs text-slate-500">{balao.data}</p>
              </div>
            </div>
            <p className="text-base font-mono font-semibold text-slate-900">
              {formatCurrency(balao.valor)}
            </p>
          </div>
        );

        return (
          <Card className="bg-white border-slate-200">
            <CardHeader className="pb-4">
              <CardTitle className="flex items-center gap-2 text-lg text-slate-800">
                <DollarSign className="text-amber-600" size={20} />
                Balões e Reforços
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-5">

              {/* Grupo 1: Marcos da Construção */}
              {marcos.length > 0 && (
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <h4 className="text-xs font-semibold uppercase tracking-wider text-amber-700">
                      Marcos da Construção
                    </h4>
                    <span className="text-xs text-slate-500">{marcos.length} marco{marcos.length > 1 ? 's' : ''}</span>
                  </div>
                  <CollapsibleBaloes
                    items={marcos}
                    limit={5}
                    accentClass="text-amber-700"
                    renderItem={(b, i) => <BalaoRow key={`m-${i}`} balao={b} isMarco />}
                  />
                  <div className="flex justify-between pt-2 mt-2 border-t border-amber-100">
                    <span className="text-xs font-medium text-amber-700">Subtotal Marcos</span>
                    <span className="text-sm font-mono font-semibold text-amber-900">{formatCurrency(totalMarcos)}</span>
                  </div>
                </div>
              )}

              {/* Grupo 2: Parcelas da Entrada (divisão do ato) */}
              {divisao.length > 0 && (
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <h4 className="text-xs font-semibold uppercase tracking-wider text-red-700">
                      Parcelas da Entrada
                    </h4>
                    <span className="text-xs text-slate-500">{divisao.length} parcela{divisao.length > 1 ? 's' : ''}</span>
                  </div>
                  <CollapsibleBaloes
                    items={divisao}
                    limit={5}
                    accentClass="text-red-700"
                    renderItem={(b, i) => (
                      <div
                        key={`d-${i}`}
                        data-testid={`balao-divisao_ato-${b.mes}`}
                        className="flex items-center justify-between p-3 rounded-lg border bg-red-50 border-red-200"
                      >
                        <div className="flex items-center gap-3">
                          <span className="text-xs font-bold px-2 py-1 rounded bg-red-200 text-red-800">Mês {b.mes}</span>
                          <div>
                            <p className="text-sm font-medium text-slate-800">{b.descricao}</p>
                            <p className="text-xs text-slate-500">{b.data}</p>
                          </div>
                        </div>
                        <p className="text-base font-mono font-semibold text-slate-900">{formatCurrency(b.valor)}</p>
                      </div>
                    )}
                  />
                  <div className="flex justify-between pt-2 mt-2 border-t border-red-100">
                    <span className="text-xs font-medium text-red-700">Subtotal Entrada</span>
                    <span className="text-sm font-mono font-semibold text-red-900">{formatCurrency(totalDivisao)}</span>
                  </div>
                </div>
              )}

              {/* Grupo 3: Reforços (anuais/semestrais/trimestrais/custom) */}
              {reforcos.length > 0 && (
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <h4 className="text-xs font-semibold uppercase tracking-wider text-slate-600">
                      {tipoReforcoLabel}
                    </h4>
                    <span className="text-xs text-slate-500">{reforcos.length} parcela{reforcos.length > 1 ? 's' : ''}</span>
                  </div>
                  <CollapsibleBaloes
                    items={reforcos}
                    limit={5}
                    accentClass="text-slate-700"
                    renderItem={(b, i) => <BalaoRow key={`r-${i}`} balao={b} isMarco={false} />}
                  />
                  <div className="flex justify-between pt-2 mt-2 border-t border-slate-200">
                    <span className="text-xs font-medium text-slate-600">Subtotal</span>
                    <span className="text-sm font-mono font-semibold text-slate-800">{formatCurrency(totalReforcos)}</span>
                  </div>
                </div>
              )}

              {/* Total Geral */}
              <div className="flex justify-between pt-3 border-t-2 border-slate-300">
                <span className="text-sm font-bold text-slate-800">Total (Marcos + Entrada + Reforços)</span>
                <span className="text-base font-mono font-bold text-slate-900">{formatCurrency(totalGeral)}</span>
              </div>
            </CardContent>
          </Card>
        );
      })()}

      {/* Slider Interativo para Explorar Meses */}
      <Card className="bg-white border-slate-200 border-2 border-purple-400">
        <CardHeader className="pb-4 bg-purple-50">
          <CardTitle className="flex items-center gap-2 text-lg text-purple-800">
            <GripVertical className="text-purple-600" size={20} />
            Explorar por Mês (Arraste o Ponto)
          </CardTitle>
        </CardHeader>
        <CardContent className="pt-6">
          {/* Slider Bar */}
          <div 
            ref={sliderRef}
            className="relative w-full h-8 bg-slate-200 rounded-full cursor-pointer select-none mb-4"
            onMouseDown={handleMouseDown}
            onMouseMove={handleMouseMove}
            onMouseUp={handleMouseUp}
            onMouseLeave={handleMouseUp}
            onTouchStart={handleTouchStart}
            onTouchMove={handleTouchMove}
            onTouchEnd={handleMouseUp}
          >
            {/* Progress fill */}
            <div 
              className="absolute top-0 left-0 h-full bg-gradient-to-r from-purple-500 to-purple-600 rounded-full transition-all duration-75"
              style={{ width: `${((selectedMes || 1) / formData.prazo_meses) * 100}%` }}
            />
            
            {/* Marcos indicators */}
            {result.meio_torre_mes && (
              <div 
                className="absolute top-0 w-1 h-full bg-blue-500 z-10"
                style={{ left: `${(result.meio_torre_mes / formData.prazo_meses) * 100}%` }}
                title={`Meio Torre: Mês ${result.meio_torre_mes}`}
              />
            )}
            {result.conclusao_obra_mes && (
              <div 
                className="absolute top-0 w-1 h-full bg-emerald-500 z-10"
                style={{ left: `${(result.conclusao_obra_mes / formData.prazo_meses) * 100}%` }}
                title={`Chaves: Mês ${result.conclusao_obra_mes}`}
              />
            )}
            
            {/* Draggable point */}
            <div 
              className="absolute top-1/2 -translate-y-1/2 w-6 h-6 bg-white border-4 border-purple-600 rounded-full shadow-lg z-20 cursor-grab active:cursor-grabbing transition-all duration-75"
              style={{ left: `calc(${((selectedMes || 1) / formData.prazo_meses) * 100}% - 12px)` }}
            >
              <div className="absolute -top-8 left-1/2 -translate-x-1/2 bg-purple-700 text-white text-xs font-bold px-2 py-1 rounded whitespace-nowrap">
                Mês {selectedMes || 1}
              </div>
            </div>
          </div>
          
          {/* Labels */}
          <div className="flex justify-between text-xs text-slate-500 mb-4">
            <span>Mês 1</span>
            {result.meio_torre_mes && (
              <span className="text-blue-600 font-medium">Meio Torre ({result.meio_torre_mes})</span>
            )}
            {result.conclusao_obra_mes && (
              <span className="text-primary font-medium">Chaves ({result.conclusao_obra_mes})</span>
            )}
            <span>Mês {formData.prazo_meses}</span>
          </div>

          {/* Dados do mês selecionado */}
          {mesSelecionado && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 p-4 bg-purple-50 rounded-lg border border-purple-200 animate-fade-in">
              <div className="text-center">
                <Calendar className="mx-auto mb-1 text-purple-500" size={16} />
                <p className="text-[10px] uppercase tracking-wider text-purple-600 mb-1">Data</p>
                <p className="text-sm font-semibold text-purple-900">{mesSelecionado.data}</p>
              </div>
              <div className="text-center">
                <Wallet className="mx-auto mb-1 text-purple-500" size={16} />
                <p className="text-[10px] uppercase tracking-wider text-purple-600 mb-1">Total Investido</p>
                <p className="text-sm font-mono font-semibold text-purple-900">{formatCurrency(mesSelecionado.total_investido_corrigido)}</p>
              </div>
              <div className="text-center">
                <Building className="mx-auto mb-1 text-purple-500" size={16} />
                <p className="text-[10px] uppercase tracking-wider text-purple-600 mb-1">Valor Imóvel</p>
                <p className="text-sm font-mono font-semibold text-primary">{formatCurrency(mesSelecionado.valor_imovel)}</p>
              </div>
              <div className="text-center">
                <PiggyBank className="mx-auto mb-1 text-purple-500" size={16} />
                <p className="text-[10px] uppercase tracking-wider text-purple-600 mb-1">Lucro Líquido</p>
                <p className={`text-sm font-mono font-semibold ${mesSelecionado.lucro_liquido >= 0 ? 'text-primary' : 'text-red-600'}`}>
                  {formatCurrency(mesSelecionado.lucro_liquido)}
                </p>
              </div>
              <div className="text-center">
                <CreditCard className="mx-auto mb-1 text-purple-500" size={16} />
                <p className="text-[10px] uppercase tracking-wider text-purple-600 mb-1">Parcela Corrigida</p>
                <p className="text-sm font-mono font-semibold text-slate-800">{formatCurrency(mesSelecionado.parcela_corrigida)}</p>
              </div>
              <div className="text-center">
                <CircleDollarSign className="mx-auto mb-1 text-purple-500" size={16} />
                <p className="text-[10px] uppercase tracking-wider text-purple-600 mb-1">Saldo Devedor</p>
                <p className="text-sm font-mono font-semibold text-slate-800">{formatCurrency(mesSelecionado.saldo_devedor)}</p>
              </div>
              <div className="text-center">
                <TrendingUp className="mx-auto mb-1 text-purple-500" size={16} />
                <p className="text-[10px] uppercase tracking-wider text-purple-600 mb-1">Ganho Capital</p>
                <p className="text-sm font-mono font-semibold text-primary">{formatCurrency(mesSelecionado.ganho_capital_acumulado)}</p>
              </div>
              <div className="text-center">
                <Percent className="mx-auto mb-1 text-purple-500" size={16} />
                <p className="text-[10px] uppercase tracking-wider text-purple-600 mb-1">% Sobre C.I.</p>
                <p className="text-sm font-mono font-semibold text-primary">{formatPercent(mesSelecionado.percentual_sobre_ci)}</p>
              </div>
            </div>
          )}
          
          {!selectedMes && (
            <p className="text-center text-sm text-purple-600 py-4">
              Arraste o ponto acima para ver os dados de qualquer mês
            </p>
          )}
        </CardContent>
      </Card>

      {/* Análise no Marco - Meio da Torre (dinâmico) */}
      {mesMeioTorre && (
        <Card className="bg-white border-slate-200 border-2 border-blue-400">
          <CardHeader className="pb-4 bg-blue-50">
            <CardTitle className="flex items-center gap-2 text-lg text-blue-800">
              <Target className="text-blue-600" size={20} />
              Meio da Torre (Mês {result.meio_torre_mes})
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-6">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
              <div className="text-center">
                <p className="data-label">Parcela Corrigida</p>
                <p className="text-xl font-mono font-semibold text-slate-800">{formatCurrency(mesMeioTorre.parcela_corrigida)}</p>
              </div>
              <div className="text-center">
                <p className="data-label">Total Investido</p>
                <p className="text-xl font-mono font-semibold text-slate-800">{formatCurrency(mesMeioTorre.total_investido_corrigido)}</p>
              </div>
              <div className="text-center">
                <p className="data-label">Valor Imóvel</p>
                <p className="text-xl font-mono font-semibold text-primary">{formatCurrency(mesMeioTorre.valor_imovel)}</p>
              </div>
              <div className="text-center">
                <p className="data-label">Lucro Líquido</p>
                <p className="text-xl font-mono font-semibold text-primary">{formatCurrency(mesMeioTorre.lucro_liquido)}</p>
              </div>
            </div>
            <div className="grid grid-cols-3 gap-4">
              <div className="text-center p-3 bg-blue-50 rounded-lg">
                <p className="data-label">Ganho Capital</p>
                <p className="text-lg font-mono font-semibold text-blue-800">{formatCurrency(mesMeioTorre.ganho_capital_acumulado)}</p>
              </div>
              <div className="text-center p-3 bg-blue-50 rounded-lg">
                <p className="data-label">% Sobre C.I.</p>
                <p className="text-lg font-mono font-semibold text-blue-800">{formatPercent(mesMeioTorre.percentual_sobre_ci)}</p>
              </div>
              <div className="text-center p-3 bg-blue-50 rounded-lg">
                <p className="data-label">Ganho Real</p>
                <p className="text-lg font-mono font-semibold text-blue-800">{formatCurrency(mesMeioTorre.ganho_real)}</p>
              </div>
            </div>
            <TotalSimuladoBar mesAtual={mesMeioTorre} />
          </CardContent>
        </Card>
      )}

      {/* Análise no Marco - Entrega das Chaves (dinâmico) */}
      {mesChaves && mesChaves.mes !== mesFinal?.mes && (
        <Card className="bg-white border-slate-200 border-2 border-emerald-500">
          <CardHeader className="pb-4 bg-emerald-50">
            <CardTitle className="flex items-center gap-2 text-lg text-primary">
              <Target className="text-primary" size={20} />
              Entrega das Chaves (Mês {result.conclusao_obra_mes})
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-6">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
              <div className="text-center">
                <p className="data-label">Parcela Corrigida</p>
                <p className="text-xl font-mono font-semibold text-slate-800">{formatCurrency(mesChaves.parcela_corrigida)}</p>
              </div>
              <div className="text-center">
                <p className="data-label">Total Investido</p>
                <p className="text-xl font-mono font-semibold text-slate-800">{formatCurrency(mesChaves.total_investido_corrigido)}</p>
              </div>
              <div className="text-center">
                <p className="data-label">Valor Imóvel</p>
                <p className="text-xl font-mono font-semibold text-primary">{formatCurrency(mesChaves.valor_imovel)}</p>
              </div>
              <div className="text-center">
                <p className="data-label">Lucro Líquido</p>
                <p className="text-xl font-mono font-semibold text-primary">{formatCurrency(mesChaves.lucro_liquido)}</p>
              </div>
            </div>
            <div className="grid grid-cols-3 gap-4">
              <div className="text-center p-3 bg-emerald-50 rounded-lg">
                <p className="data-label">Ganho Capital</p>
                <p className="text-lg font-mono font-semibold text-primary">{formatCurrency(mesChaves.ganho_capital_acumulado)}</p>
              </div>
              <div className="text-center p-3 bg-emerald-50 rounded-lg">
                <p className="data-label">% Sobre C.I.</p>
                <p className="text-lg font-mono font-semibold text-primary">{formatPercent(mesChaves.percentual_sobre_ci)}</p>
              </div>
              <div className="text-center p-3 bg-emerald-50 rounded-lg">
                <p className="data-label">Ganho Real</p>
                <p className="text-lg font-mono font-semibold text-primary">{formatCurrency(mesChaves.ganho_real)}</p>
              </div>
            </div>
            <TotalSimuladoBar mesAtual={mesChaves} />
          </CardContent>
        </Card>
      )}

      {/* Análise no Mês 36 (Meio da Torre) — REMOVIDO: Análise completa é feita no final do pagamento (card Resultado Final abaixo) */}

      {/* Resumo Final — Análise no último mês do pagamento */}
      <Card className="bg-gradient-to-br from-emerald-900 to-emerald-950 text-white border-0">
        <CardContent className="p-6">
          <h3 className="text-lg font-semibold mb-4 text-emerald-100">
            Resultado Final em {formData.prazo_meses} meses
          </h3>

          {/* TIMELINE de correção: CUB durante obra · IPCA pós-obra */}
          {(() => {
            const mesFimObra = result.conclusao_obra_mes || 0;
            const prazoTotal = formData.prazo_meses;
            const projObra = mesFimObra > 0 ? result.projecoes.find(p => p.mes === mesFimObra) : null;
            const indiceObra = projObra?.indice_aplicado || formData.correcao_construcao_indice || 'CUB';
            const indicePos = mesFinal?.indice_aplicado || formData.correcao_pos_indice || 'IPCA';
            const taxaObra = projObra?.taxa_anual_aplicada;
            const taxaPos = mesFinal?.taxa_anual_aplicada;
            // Sem obra (todo prazo é pós) — mostra timeline simplificada
            if (mesFimObra <= 0) {
              return (
                <div className="mb-5 p-3 bg-emerald-950/60 rounded-lg border border-emerald-700/50">
                  <div className="flex items-center gap-2 text-xs text-emerald-200">
                    <Calendar size={14} />
                    <span>Correção aplicada em todos os <strong>{prazoTotal} meses</strong>:</span>
                    <span className="px-2 py-0.5 rounded bg-blue-600 text-white font-bold">
                      {indicePos}{taxaPos ? ` ${taxaPos}% a.a.` : ''}
                    </span>
                  </div>
                </div>
              );
            }
            const pctObra = (mesFimObra / prazoTotal) * 100;
            const mesesPos = prazoTotal - mesFimObra;
            return (
              <div className="mb-5 p-4 bg-emerald-950/60 rounded-lg border border-emerald-700/50">
                <div className="flex items-center gap-2 mb-3 text-xs uppercase tracking-wider text-emerald-200 font-semibold">
                  <Calendar size={14} />
                  Estrutura de Correção
                </div>
                {/* Barra timeline */}
                <div className="relative h-9 bg-slate-800 rounded-lg overflow-hidden flex">
                  <div
                    className="bg-amber-600 flex items-center justify-center text-[11px] font-bold text-white border-r-2 border-emerald-950"
                    style={{ width: `${pctObra}%` }}
                  >
                    {pctObra > 18 && (
                      <span>{indiceObra}{taxaObra ? ` ${taxaObra}%` : ''}</span>
                    )}
                  </div>
                  <div
                    className="bg-blue-600 flex items-center justify-center text-[11px] font-bold text-white"
                    style={{ width: `${100 - pctObra}%` }}
                  >
                    {(100 - pctObra) > 18 && (
                      <span>{indicePos}{taxaPos ? ` ${taxaPos}%` : ''}</span>
                    )}
                  </div>
                </div>
                {/* Labels com mês */}
                <div className="flex justify-between mt-1.5 text-[10px] text-emerald-300">
                  <span>Mês 1</span>
                  <span className="text-amber-300 font-semibold">↑ Conclusão da obra (mês {mesFimObra})</span>
                  <span>Mês {prazoTotal}</span>
                </div>
                {/* Detalhamento textual */}
                <div className="grid grid-cols-2 gap-3 mt-3 text-xs">
                  <div className="p-2 bg-amber-900/40 rounded border-l-4 border-amber-500">
                    <div className="flex items-center gap-1 mb-0.5">
                      <span className="px-1.5 py-0.5 rounded bg-amber-600 text-white text-[10px] font-bold">{indiceObra}</span>
                      <span className="text-amber-100 font-medium">Durante a obra</span>
                    </div>
                    <p className="text-amber-200/90">
                      Mês 1 → mês {mesFimObra} <span className="text-amber-300">({mesFimObra} meses)</span>
                    </p>
                    {taxaObra != null && <p className="text-amber-200/70 text-[11px]">Taxa: {taxaObra}% a.a.</p>}
                  </div>
                  <div className="p-2 bg-blue-900/40 rounded border-l-4 border-blue-500">
                    <div className="flex items-center gap-1 mb-0.5">
                      <span className="px-1.5 py-0.5 rounded bg-blue-600 text-white text-[10px] font-bold">{indicePos}</span>
                      <span className="text-blue-100 font-medium">Após a obra</span>
                    </div>
                    <p className="text-blue-200/90">
                      Mês {mesFimObra + 1} → mês {prazoTotal} <span className="text-blue-300">({mesesPos} meses)</span>
                    </p>
                    {taxaPos != null && <p className="text-blue-200/70 text-[11px]">Taxa: {taxaPos}% a.a.</p>}
                  </div>
                </div>
                <p className="text-[11px] text-emerald-300/80 mt-2 italic">
                  ⚠️ {indiceObra} corrige <strong>somente até o mês {mesFimObra}</strong> (entrega das chaves). De lá em diante, o saldo é corrigido por {indicePos}.
                </p>
              </div>
            );
          })()}

          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            <div className="text-center">
              <Building className="mx-auto mb-2 text-emerald-300" size={28} />
              <p className="text-xs uppercase tracking-wider text-emerald-200 mb-1">Valor Imóvel</p>
              <p className="text-2xl font-mono font-bold">
                {formatCurrency(result.valor_imovel_final)}
              </p>
            </div>
            
            <div className="text-center">
              <TrendingUp className="mx-auto mb-2 text-emerald-300" size={28} />
              <p className="text-xs uppercase tracking-wider text-emerald-200 mb-1">Ganho Capital</p>
              <p className="text-2xl font-mono font-bold">
                {formatCurrency(result.ganho_capital_final)}
              </p>
            </div>
            
            <div className="text-center">
              <BarChart3 className="mx-auto mb-2 text-emerald-300" size={28} />
              <p className="text-xs uppercase tracking-wider text-emerald-200 mb-1">% sobre C.I.</p>
              <p className="text-2xl font-mono font-bold">
                {formatPercent(result.percentual_sobre_ci_final)}
              </p>
            </div>
            
            <div className="text-center">
              <PiggyBank className="mx-auto mb-2 text-emerald-300" size={28} />
              <p className="text-xs uppercase tracking-wider text-emerald-200 mb-1">Lucro Líquido</p>
              <p className="text-2xl font-mono font-bold">
                {formatCurrency(result.lucro_liquido_final)}
              </p>
            </div>
          </div>

          {/* Detalhamento do Total Investido — base + correção CUB (obra) + correção IPCA/IGP-M (pós-obra) */}
          {(() => {
            const investidoCorrigido = mesFinal?.total_investido_corrigido || 0;
            const investidoBase = mesFinal?.total_investido_original || 0;
            const correcaoTotal = investidoCorrigido - investidoBase;
            // Última projeção na obra (corrigida por CUB) e final (corrigida pelo pós-obra)
            const mesFimObra = result.conclusao_obra_mes || 0;
            const projObra = mesFimObra > 0 ? result.projecoes.find(p => p.mes === mesFimObra) : null;
            const investidoBaseObra = projObra?.total_investido_original || 0;
            const investidoCorrObra = projObra?.total_investido_corrigido || 0;
            const correcaoObra = investidoCorrObra - investidoBaseObra;
            const correcaoPos = correcaoTotal - correcaoObra;
            const indiceObra = projObra?.indice_aplicado || 'CUB';
            const indicePos = mesFinal?.indice_aplicado || 'IPCA';
            return (
              <div className="mt-5 p-4 bg-emerald-950/60 rounded-lg border border-emerald-700/50">
                <div className="flex items-center justify-between mb-3">
                  <h4 className="text-xs uppercase tracking-wider text-emerald-200 font-semibold">
                    Total Investido ao final ({formData.prazo_meses} meses)
                  </h4>
                  <span className="text-xl font-mono font-bold text-white">
                    {formatCurrency(investidoCorrigido)}
                  </span>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3 text-xs">
                  <div className="p-2 bg-emerald-900/60 rounded">
                    <p className="text-emerald-300 mb-1">Valores Originais (sem correção)</p>
                    <p className="font-mono font-bold text-white">{formatCurrency(investidoBase)}</p>
                  </div>
                  {mesFimObra > 0 && (
                    <div className="p-2 bg-amber-900/50 rounded">
                      <p className="text-amber-200 mb-1">+ Correção {indiceObra} (durante obra)</p>
                      <p className="font-mono font-bold text-amber-100">+ {formatCurrency(correcaoObra)}</p>
                    </div>
                  )}
                  <div className="p-2 bg-blue-900/50 rounded">
                    <p className="text-blue-200 mb-1">
                      + Correção {indicePos} {mesFimObra > 0 ? '(pós-obra)' : ''}
                    </p>
                    <p className="font-mono font-bold text-blue-100">+ {formatCurrency(correcaoPos)}</p>
                  </div>
                </div>
                <p className="text-[11px] text-emerald-300/80 mt-2">
                  Total Investido = Valores originais + correções aplicadas mês a mês conforme o período (obra/pós-obra).
                </p>
              </div>
            );
          })()}
        </CardContent>
      </Card>

      {/* Financiamento Pós-Obra - Mostra APENAS quando a modalidade "bancário" foi escolhida
          E o prazo excede a conclusão da obra */}
      {financiamentoPosObra > 0 && formData.tipo_financiamento === 'bancario' && (
        <Card className="bg-white border-slate-200 border-2 border-orange-500">
          <CardHeader className="pb-4 bg-orange-50">
            <CardTitle className="flex items-center gap-2 text-lg text-orange-800">
              <Landmark className="text-orange-600" size={20} />
              Financiamento Bancário Pós-Obra
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-6">
            <div className="p-4 bg-orange-100 rounded-lg border border-orange-300 mb-4">
              <p className="text-sm text-orange-800">
                O prazo de pagamento ({formData.prazo_meses} meses) excede a conclusão da obra (mês {conclusaoObraMes}).
                Os <strong>{mesesPosObra} meses restantes</strong> correspondem ao financiamento bancário após a entrega das chaves.
              </p>
            </div>
            
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="text-center p-4 bg-orange-50 rounded-lg border border-orange-200">
                <Clock className="mx-auto mb-1 text-orange-600" size={20} />
                <p className="text-xs uppercase tracking-wider text-orange-700 mb-1">Meses Pós-Obra</p>
                <p className="text-2xl font-mono font-bold text-orange-800">{mesesPosObra}</p>
                <p className="text-xs text-orange-600">meses de financiamento</p>
              </div>
              
              <div className="text-center p-4 bg-orange-50 rounded-lg border border-orange-200">
                <CircleDollarSign className="mx-auto mb-1 text-orange-600" size={20} />
                <p className="text-xs uppercase tracking-wider text-orange-700 mb-1">Valor Financiado</p>
                <p className="text-xl font-mono font-bold text-orange-800">{formatCurrency(financiamentoPosObra)}</p>
                <p className="text-xs text-orange-600">{formatPercent(financiamentoPosObraPercentual, 2)} do V.C.</p>
              </div>
              
              <div className="text-center p-4 bg-orange-50 rounded-lg border border-orange-200">
                <CreditCard className="mx-auto mb-1 text-orange-600" size={20} />
                <p className="text-xs uppercase tracking-wider text-orange-700 mb-1">Parcela Mensal</p>
                <p className="text-xl font-mono font-bold text-orange-800">{formatCurrency(result.parcela_mensal)}</p>
                <p className="text-xs text-orange-600">durante o financiamento</p>
              </div>
              
              <div className="text-center p-4 bg-slate-100 rounded-lg border border-slate-300">
                <ClipboardCheck className="mx-auto mb-1 text-slate-500" size={20} />
                <p className="text-xs uppercase tracking-wider text-slate-600 mb-1">Pago até Chaves</p>
                <p className="text-xl font-mono font-bold text-slate-800">{formatCurrency(result.total_proposta - financiamentoPosObra)}</p>
                <p className="text-xs text-slate-600">{formatPercent(100 - financiamentoPosObraPercentual, 2)}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Rendimento com Locação - SEMPRE mostra estimativa de retorno */}
      <Card className="bg-gradient-to-br from-blue-900 to-blue-950 text-white border-0">
        <CardContent className="p-6">
          <h3 className="text-lg font-semibold mb-4 text-blue-100 flex items-center gap-2">
            <Home size={22} className="text-blue-300" />
            Estimativa de Retorno com Locação
            {financiamentoPosObra > 0 && formData.tipo_financiamento === 'bancario' && (
              <span className="ml-2 px-2 py-1 bg-orange-500/80 text-white text-xs rounded-full">
                Financiamento Pós-Obra
              </span>
            )}
          </h3>
          
          {(() => {
            // Referência: se tem conclusão de obra, usa esse mês; senão usa o prazo final
            const mesRefEntrega = conclusaoObraMes > 0 ? conclusaoObraMes : formData.prazo_meses;
            const mesInicio = mesRefEntrega + 1;
            const mesesLocacaoSimulado = conclusaoObraMes > 0 
              ? Math.max(0, formData.prazo_meses - conclusaoObraMes)
              : 0;
            
            const projecaoRef = result.projecoes.find(p => p.mes === mesRefEntrega) || mesFinal;
            const valorImovelRef = projecaoRef?.valor_imovel || result.valor_imovel_final;
            const taxaLocacao = formData.locacao_percentual || 0.5;
            const aluguelMensal = formData.locacao_valor_fixo > 0 
              ? formData.locacao_valor_fixo 
              : (valorImovelRef * taxaLocacao) / 100;
            
            // Projeção para diferentes períodos
            const rendimento12m = aluguelMensal * 12;
            const rendimento24m = aluguelMensal * 24;
            const rendimento60m = aluguelMensal * 60;
            const rendimentoSimulado = aluguelMensal * mesesLocacaoSimulado;
            const lucroComLocacao = result.lucro_liquido_final + rendimentoSimulado;
            
            // Índice de Retorno com Locação (quando há financiamento pós-obra)
            // No modo bancário: parcela pós-obra (valor financiado do banco).
            // No modo direto: parcela mensal do comprador (continua após a entrega).
            // FIX v7.1.12: usa a parcela CORRIGIDA do mês de início da locação (primeiro
            // mês pós-obra), não o valor original, para comparação justa com o aluguel.
            const projecaoInicioLocacao = result.projecoes.find(p => p.mes === mesInicio) || projecaoRef;
            const parcelaCorrigidaInicioLocacao = projecaoInicioLocacao?.parcela_corrigida || result.parcela_mensal || 0;
            const parcelaFinanciamento = formData.tipo_financiamento === 'bancario'
              ? (financiamentoPosObra > 0 ? result.parcela_mensal : 0)
              : parcelaCorrigidaInicioLocacao;
            const saldoMensalLocacao = aluguelMensal - parcelaFinanciamento;
            const indiceRetornoLocacao = parcelaFinanciamento > 0 
              ? ((aluguelMensal / parcelaFinanciamento) * 100)
              : 0;
            const cobreFinanciamento = aluguelMensal >= parcelaFinanciamento;

            // Retorno real sobre capital investido (não a taxa sobre o imóvel)
            const totalInvestidoRef = projecaoRef?.total_investido_corrigido || 0;
            const retornoMensalSobreCapital = totalInvestidoRef > 0
              ? (aluguelMensal / totalInvestidoRef) * 100
              : 0;
            const retornoAnualSobreCapital = retornoMensalSobreCapital * 12;
            // Mantido para compatibilidade interna
            const retornoAnualLocacao = retornoAnualSobreCapital.toFixed(2);
            
            return (
              <>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                  <div className="text-center p-3 bg-blue-800/50 rounded-lg">
                    <Calendar className="mx-auto mb-1 text-blue-300" size={20} />
                    <p className="text-[10px] uppercase tracking-wider text-blue-200 mb-1">
                      {conclusaoObraMes > 0 ? 'Início da Locação' : 'Referência'}
                    </p>
                    <p className="text-lg font-mono font-semibold">
                      {conclusaoObraMes > 0 ? `Mês ${mesInicio}` : `Mês ${mesRefEntrega}`}
                    </p>
                    <p className="text-[10px] text-blue-300">
                      {conclusaoObraMes > 0 ? 'após entrega' : 'final do prazo'}
                    </p>
                  </div>
                  
                  <div className="text-center p-3 bg-blue-800/50 rounded-lg">
                    <Building className="mx-auto mb-1 text-blue-300" size={20} />
                    <p className="text-[10px] uppercase tracking-wider text-blue-200 mb-1">Valor do Imóvel</p>
                    <p className="text-lg font-mono font-bold">{formatCurrency(valorImovelRef)}</p>
                    <p className="text-[10px] text-blue-300">
                      {conclusaoObraMes > 0 ? 'na entrega' : 'ao final do prazo'}
                    </p>
                  </div>
                  
                  <div className="text-center p-3 bg-blue-800/50 rounded-lg">
                    <Percent className="mx-auto mb-1 text-blue-300" size={20} />
                    <p className="text-[10px] uppercase tracking-wider text-blue-200 mb-1">Taxa de Locação</p>
                    <p className="text-xl font-mono font-bold">{retornoMensalSobreCapital.toFixed(2)}% <span className="text-sm font-normal">a.m.</span></p>
                    <p className="text-xs text-blue-200 mt-0.5">{retornoAnualSobreCapital.toFixed(2)}% a.a. sobre capital</p>
                    <p className="text-xs text-blue-300 mt-0.5">Aluguel: {taxaLocacao}% s/ imóvel</p>
                    <p className="text-[10px] text-blue-300">ao mês</p>
                  </div>
                  
                  <div className="text-center p-3 bg-green-800/60 rounded-lg border border-green-500/50">
                    <Key className="mx-auto mb-1 text-green-300" size={20} />
                    <p className="text-[10px] uppercase tracking-wider text-green-200 mb-1">Aluguel Mensal</p>
                    <p className="text-xl font-mono font-bold text-green-300">{formatCurrency(aluguelMensal)}</p>
                    <p className="text-[10px] text-green-300">estimado</p>
                  </div>
                </div>
                
                {/* Índice de Retorno com Locação x Financiamento - SOMENTE quando há financiamento pós-obra */}
                {financiamentoPosObra > 0 && (
                  <div className="mb-4 p-4 bg-gradient-to-r from-orange-900/60 to-yellow-900/40 rounded-lg border-2 border-orange-400">
                    <h4 className="text-sm font-bold text-orange-200 mb-3 flex items-center gap-2">
                      <BarChart3 size={18} className="text-orange-300" />
                      Índice de Retorno com Locação x Financiamento
                    </h4>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                      <div className="text-center p-3 bg-orange-950/50 rounded-lg">
                        <CreditCard className="mx-auto mb-1 text-orange-300" size={18} />
                        <p className="text-[10px] uppercase tracking-wider text-orange-200 mb-1">Parcela Financ.</p>
                        <p className="text-lg font-mono font-bold text-orange-300">{formatCurrency(parcelaFinanciamento)}</p>
                      </div>
                      <div className="text-center p-3 bg-green-950/50 rounded-lg">
                        <Key className="mx-auto mb-1 text-green-300" size={18} />
                        <p className="text-[10px] uppercase tracking-wider text-green-200 mb-1">Aluguel</p>
                        <p className="text-lg font-mono font-bold text-green-300">{formatCurrency(aluguelMensal)}</p>
                      </div>
                      <div className={`text-center p-3 rounded-lg ${cobreFinanciamento ? 'bg-green-800/60' : 'bg-red-800/60'}`}>
                        <DollarSign className={`mx-auto mb-1 ${cobreFinanciamento ? 'text-green-300' : 'text-red-300'}`} size={18} />
                        <p className="text-[10px] uppercase tracking-wider text-white/80 mb-1">Saldo Mensal</p>
                        <p className={`text-lg font-mono font-bold ${cobreFinanciamento ? 'text-green-300' : 'text-red-300'}`}>
                          {saldoMensalLocacao >= 0 ? '+' : ''}{formatCurrency(saldoMensalLocacao)}
                        </p>
                      </div>
                      <div className={`text-center p-3 rounded-lg border-2 ${cobreFinanciamento ? 'bg-emerald-700 border-emerald-400' : 'bg-red-700 border-red-400'}`}>
                        <LineChart className="mx-auto mb-1 text-white" size={18} />
                        <p className="text-[10px] uppercase tracking-wider text-white/90 mb-1">Índice Retorno</p>
                        <p className="text-2xl font-mono font-bold text-white">{formatPercent(indiceRetornoLocacao, 1)}</p>
                        <p className="text-[10px] text-white/80">{cobreFinanciamento ? 'COBRE' : 'NÃO COBRE'}</p>
                      </div>
                    </div>
                    
                    {/* Análise Detalhada */}
                    <div className={`mt-3 p-3 rounded-lg ${cobreFinanciamento ? 'bg-green-900/50' : 'bg-red-900/50'}`}>
                      {cobreFinanciamento ? (
                        <p className="text-sm text-green-200">
                          <strong>Excelente!</strong> O aluguel de <strong>{formatCurrency(aluguelMensal)}</strong> cobre 
                          a parcela do financiamento de <strong>{formatCurrency(parcelaFinanciamento)}</strong> e ainda sobra 
                          <strong className="text-green-300"> {formatCurrency(saldoMensalLocacao)}/mês</strong>.
                          Em {mesesPosObra} meses, você terá um ganho extra de 
                          <strong className="text-green-300"> {formatCurrency(saldoMensalLocacao * mesesPosObra)}</strong>.
                        </p>
                      ) : (
                        <p className="text-sm text-red-200">
                          <strong>Atenção!</strong> O aluguel de <strong>{formatCurrency(aluguelMensal)}</strong> não cobre 
                          a parcela do financiamento de <strong>{formatCurrency(parcelaFinanciamento)}</strong>. 
                          Você precisará desembolsar <strong className="text-red-300">{formatCurrency(Math.abs(saldoMensalLocacao))}/mês</strong>.
                          Em {mesesPosObra} meses, isso representa um custo adicional de 
                          <strong className="text-red-300"> {formatCurrency(Math.abs(saldoMensalLocacao) * mesesPosObra)}</strong>.
                        </p>
                      )}
                    </div>
                  </div>
                )}
                
                {/* Projeção de Diferença entre Aluguel e Parcela (quando ainda tem parcelas) */}
                {parcelaFinanciamento > 0 && (
                  <div className="mt-4 p-4 bg-blue-950/50 rounded-lg">
                    <h4 className="text-sm font-semibold text-blue-200 mb-3">Diferença entre Aluguel e Financiamento</h4>
                    <p className="text-xs text-blue-300 mb-3">Valor de diferença da parcela restante do financiamento e do rendimento estimado com locação</p>
                    <div className="grid grid-cols-3 gap-4">
                      <div className="text-center">
                        <p className="text-xs text-blue-300 mb-1">1 ano (12 meses)</p>
                        <p className={`text-lg font-mono font-bold ${saldoMensalLocacao >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                          {saldoMensalLocacao >= 0 ? '+' : ''}{formatCurrency(saldoMensalLocacao * 12)}
                        </p>
                      </div>
                      <div className="text-center">
                        <p className="text-xs text-blue-300 mb-1">2 anos (24 meses)</p>
                        <p className={`text-lg font-mono font-bold ${saldoMensalLocacao >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                          {saldoMensalLocacao >= 0 ? '+' : ''}{formatCurrency(saldoMensalLocacao * 24)}
                        </p>
                      </div>
                      <div className="text-center">
                        <p className="text-xs text-blue-300 mb-1">5 anos (60 meses)</p>
                        <p className={`text-lg font-mono font-bold ${saldoMensalLocacao >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                          {saldoMensalLocacao >= 0 ? '+' : ''}{formatCurrency(saldoMensalLocacao * 60)}
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                {/* Lucro com Locação dentro da simulação */}
                {mesesLocacaoSimulado > 0 && (
                  <div className="mt-4 p-4 bg-green-900/40 rounded-lg border border-green-500/30">
                    <p className="text-sm text-green-200">
                      <strong>Na sua simulação:</strong> Com {mesesLocacaoSimulado} meses de locação (do mês {mesInicio} ao {formData.prazo_meses}), 
                      o rendimento adicional é de <span className="text-green-400 font-bold">{formatCurrency(rendimentoSimulado)}</span>, 
                      totalizando um lucro de <span className="text-green-300 font-bold">{formatCurrency(lucroComLocacao)}</span>.
                    </p>
                  </div>
                )}

                {/* Resumo sem financiamento pós-obra (cenário padrão) - removido a pedido do usuário */}
              </>
            );
          })()}
        </CardContent>
      </Card>


      {/* ================================================================
          INDICADORES FINANCEIROS — TIR, VPL, Payback, Custo de Oportunidade
          ================================================================ */}
      {(result.tir_anual != null || result.vpl != null || result.payback_mes != null) && (() => {
        const tir       = result.tir_anual;
        const vpl       = result.vpl;
        const paybackMes = result.payback_mes;
        const paybackData = result.payback_data;
        const cdoFinal  = result.custo_oportunidade_valor_final;
        const cdoDelta  = result.custo_oportunidade_delta;
        const cdoPct    = result.custo_oportunidade_percentual;
        const taxaDesc  = result.taxa_desconto_usada;
        const fonteDesc = result.fonte_taxa_desconto;
        const imoveiBate = cdoDelta != null && cdoDelta > 0;

        // SAC/Price
        const temFinBanc = result.financiamento_bancario_parcelas?.length > 0;
        const cet        = result.financiamento_bancario_cet_anual;
        const parcelaIni = result.financiamento_bancario_parcela_inicial;
        const parcelaFin = result.financiamento_bancario_parcela_final;
        const totalJuros = result.financiamento_bancario_total_juros;
        const tipoAmort  = formData?.tipo_amortizacao || 'PRICE';

        return (
          <Card className="border-0 overflow-hidden shadow-lg">
            {/* Header escuro estilo EQI */}
            <div className="bg-[#04342c] px-6 py-4 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-lg bg-[#1d9e75] flex items-center justify-center">
                  <BarChart3 size={16} className="text-white" />
                </div>
                <div>
                  <h3 className="text-white font-semibold text-sm tracking-wide">Análise Financeira</h3>
                  <p className="text-[#9ee4c4] text-[11px]">TIR · VPL · Payback · Custo de Oportunidade</p>
                </div>
              </div>
              {taxaDesc && (
                <div className="text-right">
                  <p className="text-[10px] uppercase tracking-widest text-[#9ee4c4]">Taxa de referência</p>
                  <p className="text-white font-mono text-sm font-semibold">{formatPercent(taxaDesc)} a.a.</p>
                  <p className="text-[#9ee4c4] text-[10px]">{fonteDesc?.includes('fallback') ? 'CDI estimado' : (formData?.indice_desconto || 'CDI')}</p>
                </div>
              )}
            </div>

            <CardContent className="p-0">

              {/* Grid de métricas principais */}
              <div className="grid grid-cols-2 md:grid-cols-4 divide-x divide-y divide-slate-100">

                {/* TIR */}
                <div className="p-5">
                  <div className="flex items-center gap-1.5 mb-3">
                    <TrendingUp size={13} className="text-emerald-600" />
                    <span className="text-[11px] font-semibold uppercase tracking-widest text-slate-400">TIR do Investimento</span>
                  </div>
                  {tir != null ? (
                    <>
                      <p className="font-mono text-2xl font-bold text-emerald-700">{formatPercent(tir)} <span className="text-sm font-normal text-slate-400">a.a.</span></p>
                      <p className="text-[11px] text-slate-400 mt-1">Taxa interna de retorno real</p>
                      <div className={`mt-2 inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold ${tir >= (taxaDesc || 10.5) ? 'bg-emerald-50 text-emerald-700' : 'bg-red-50 text-red-600'}`}>
                        {tir >= (taxaDesc || 10.5) ? '▲ Supera referência' : '▼ Abaixo da referência'}
                      </div>
                    </>
                  ) : <p className="text-slate-400 text-sm">Não calculável</p>}
                </div>

                {/* VPL */}
                <div className="p-5">
                  <div className="flex items-center gap-1.5 mb-3">
                    <CircleDollarSign size={13} className="text-blue-600" />
                    <span className="text-[11px] font-semibold uppercase tracking-widest text-slate-400">VPL</span>
                  </div>
                  {vpl != null ? (
                    <>
                      <p className={`font-mono text-2xl font-bold ${vpl >= 0 ? 'text-blue-700' : 'text-red-600'}`}>{formatCurrency(vpl)}</p>
                      <p className="text-[11px] text-slate-400 mt-1">Valor presente líquido</p>
                      <div className={`mt-2 inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold ${vpl >= 0 ? 'bg-blue-50 text-blue-700' : 'bg-red-50 text-red-600'}`}>
                        {vpl >= 0 ? '✓ Investimento viável' : '✗ VPL negativo'}
                      </div>
                    </>
                  ) : <p className="text-slate-400 text-sm">Não calculável</p>}
                </div>

                {/* Payback */}
                <div className="p-5">
                  <div className="flex items-center gap-1.5 mb-3">
                    <Clock size={13} className="text-amber-600" />
                    <span className="text-[11px] font-semibold uppercase tracking-widest text-slate-400">Payback</span>
                  </div>
                  {paybackMes != null ? (
                    <>
                      <p className="font-mono text-2xl font-bold text-amber-700">
                        {Math.floor(paybackMes)} <span className="text-sm font-normal text-slate-400">meses</span>
                      </p>
                      {paybackData && <p className="text-[11px] text-slate-400 mt-1">Retorno em {paybackData}</p>}
                      {paybackMes > 0 && (
                        <p className="text-[11px] text-slate-400">{(paybackMes / 12).toFixed(1)} anos</p>
                      )}
                    </>
                  ) : (
                    <>
                      <p className="font-mono text-sm font-bold text-slate-400">Fora do prazo</p>
                      <p className="text-[11px] text-slate-400 mt-1">Não atinge break-even</p>
                    </>
                  )}
                </div>

                {/* Custo de Oportunidade */}
                <div className="p-5">
                  <div className="flex items-center gap-1.5 mb-3">
                    <Target size={13} className={imoveiBate ? 'text-emerald-600' : 'text-red-500'} />
                    <span className="text-[11px] font-semibold uppercase tracking-widest text-slate-400">vs. Renda Fixa</span>
                  </div>
                  {cdoFinal != null ? (
                    <>
                      <p className={`font-mono text-2xl font-bold ${imoveiBate ? 'text-emerald-700' : 'text-red-600'}`}>
                        {cdoPct != null ? (cdoPct > 0 ? '+' : '') + formatPercent(cdoPct) : '—'}
                      </p>
                      <p className="text-[11px] text-slate-400 mt-1">Imóvel vs {formData?.indice_desconto || 'CDI'}</p>
                      <p className="text-[11px] text-slate-500 mt-0.5">RF renderia {formatCurrency(cdoFinal)}</p>
                    </>
                  ) : <p className="text-slate-400 text-sm">Não calculável</p>}
                </div>
              </div>

              {/* Leitura consolidada */}
              {(tir != null && vpl != null) && (
                <div className={`mx-5 mb-5 mt-2 rounded-xl p-4 text-sm ${imoveiBate ? 'bg-emerald-50 border border-emerald-200' : 'bg-amber-50 border border-amber-200'}`}>
                  <p className={`font-semibold mb-1 ${imoveiBate ? 'text-emerald-800' : 'text-amber-800'}`}>
                    {imoveiBate ? '✓ Investimento supera a renda fixa' : '⚠ Atenção ao custo de oportunidade'}
                  </p>
                  <p className={`text-[12px] leading-relaxed ${imoveiBate ? 'text-emerald-700' : 'text-amber-700'}`}>
                    {imoveiBate
                      ? `A TIR de ${formatPercent(tir)} a.a. supera a referência de ${formatPercent(taxaDesc)} a.a., com VPL positivo de ${formatCurrency(vpl)}. O imóvel rende ${formatPercent(Math.abs(cdoPct || 0))} a mais que a renda fixa no mesmo prazo.`
                      : `Com taxa de referência de ${formatPercent(taxaDesc)} a.a., o VPL ficou negativo (${formatCurrency(vpl)}). O mesmo capital na renda fixa renderia ${formatCurrency(cdoFinal)}, superando o imóvel em ${formatPercent(Math.abs(cdoPct || 0))}.`
                    }
                  </p>
                </div>
              )}

              {/* Financiamento Bancário Real — SAC/PRICE */}
              {temFinBanc && (
                <div className="border-t border-slate-100 mx-5 mb-5 pt-4">
                  <div className="flex items-center gap-2 mb-3">
                    <Landmark size={14} className="text-slate-500" />
                    <h4 className="text-[11px] font-semibold uppercase tracking-widest text-slate-400">
                      Financiamento Bancário — {tipoAmort}
                    </h4>
                  </div>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    {[
                      { label: 'Parcela inicial', value: formatCurrency(parcelaIni), cor: 'text-slate-800' },
                      { label: 'Parcela final', value: formatCurrency(parcelaFin), cor: tipoAmort === 'SAC' ? 'text-emerald-700' : 'text-slate-800' },
                      { label: 'Total de juros', value: formatCurrency(totalJuros), cor: 'text-red-600' },
                      { label: 'CET anual', value: cet ? formatPercent(cet) + ' a.a.' : '—', cor: 'text-slate-800' },
                    ].map(({ label, value, cor }) => (
                      <div key={label} className="bg-slate-50 rounded-lg p-3">
                        <p className="text-[10px] uppercase tracking-wider text-slate-400 mb-1">{label}</p>
                        <p className={`font-mono font-semibold text-sm ${cor}`}>{value}</p>
                      </div>
                    ))}
                  </div>
                  <p className="text-[11px] text-slate-400 mt-2">
                    {tipoAmort === 'SAC'
                      ? 'SAC: parcela decresce à medida que a dívida é amortizada — juros sobre saldo devedor decrescente.'
                      : 'Price: parcela fixa durante todo o prazo — maior peso de juros no início.'}
                  </p>
                </div>
              )}

            </CardContent>
          </Card>
        );
      })()}

      {/* Explicação do Modelo */}
      <Card className="bg-amber-50 border-amber-200">
        <CardContent className="p-4">
          <h4 className="font-semibold text-amber-800 mb-2 flex items-center gap-2">
            <Percent size={18} />
            Por que o investimento imobiliário é tão atrativo?
          </h4>
          <p className="text-sm text-amber-700">
            A valorização incide sobre o <strong>valor total do imóvel ({formatCurrency(formData.valor_mercado)})</strong>, 
            não apenas sobre o capital investido. Com uma entrada de apenas {formatPercent(result.ato_sinal_percentual)} 
            e parcelas diluídas, você "compra" a valorização de um patrimônio muito maior do que o seu investimento real.
          </p>
        </CardContent>
      </Card>
    </div>
  );
};

export default ResultsSummary;
